import { memo, useMemo } from 'react';
import {
  Grid,
  Divider,
  useEventCallback,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent,
} from '@mui/material';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { PmiButton } from 'shell/components/shared/button';
import { FormikErrors, FormikTouched } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import ListCategory from 'practice/components/shared/list-category';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';

interface FormProps {
  values?: ItalianVM;
  onChange: (key: keyof ItalianVM, value: ItalianVM[keyof ItalianVM]) => void;
  errors?: FormikErrors<ItalianVM>;
  touched?: FormikTouched<ItalianVM>;
  disabled?: boolean;
  ForPracticeTemporary?: boolean;
}

const CadastralDataComponent = ({
  values,
  onChange,
  errors,
  disabled,
  ForPracticeTemporary,
  touched,
}: FormProps): React.ReactElement => {
  const isPersonHomeless = useMemo(() => {
    if (
      values?.address?.description != '' &&
      values?.address != undefined &&
      values?.number != undefined
    ) {
      return true;
    }
  }, [values?.address, values?.number]);

  const { userProfile } = useUserProfile();

  const isAuthorizedHomeless =
    userProfile?.datiLogin?.profile?.feature?.senzaFissaDimora === '1' ||
    userProfile?.datiLogin?.profile?.feature?.senzaFissaDimora === '2';

  const onChangeCategory = useEventCallback((cat: SelectChangeEvent<string>) => {
    onChange('cat', cat.target.value);
  });

  return (
    <Grid item container spacing={2}>
      <>
        <Grid item xs={12}>
          <PmiTitleDivider>Dati catastali</PmiTitleDivider>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            id="section"
            label="Sezione"
            aria-label="Sezione"
            fullWidth
            error={touched?.section && Boolean(errors?.section)}
            value={values?.section?.toUpperCase() || ''}
            onChange={(event) => {
              onChange('section', event.target.value?.toUpperCase());
            }}
            helperText={errors?.section}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            id="sheet"
            label={'Foglio'}
            aria-label="Foglio"
            fullWidth
            value={values?.sheet || ''}
            onChange={(event) => {
              onChange('sheet', event.target.value);
            }}
            helperText={errors?.sheet}
            error={touched?.sheet && Boolean(errors?.sheet)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            id="particle"
            label={'Particella'}
            aria-label="Particella"
            fullWidth
            error={touched?.particle && Boolean(errors?.particle)}
            value={values?.particle || ''}
            onChange={(event) => {
              onChange('particle', event.target.value);
            }}
            helperText={errors?.particle}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <PmiTextField
            id="sub"
            label={'Subalterno'}
            aria-label="Subalterno"
            fullWidth
            error={touched?.sub && Boolean(errors?.sub)}
            value={values?.sub || ''}
            onChange={(event) => {
              onChange('sub', event.target.value);
            }}
            helperText={errors?.sub}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          {disabled ? (
            <PmiTextField
              id="cat"
              label={'Cat. Catastale'}
              aria-label="CatCatastale"
              fullWidth
              error={touched?.cat && Boolean(errors?.cat)}
              value={values?.cat || ''}
              onChange={(event) => {
                onChange('cat', event.target.value);
              }}
              helperText={errors?.cat}
              disabled
            />
          ) : (
            <ListCategory
              value={values?.cat || ''}
              error={touched?.cat && Boolean(errors?.cat)}
              onChange={onChangeCategory}
            />
          )}
        </Grid>
      </>
      {ForPracticeTemporary || isAuthorizedHomeless ? (
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="isHomeless"
                checked={
                  (values?.isHomeless === undefined ? values?.isHomeless : values?.isHomeless) ||
                  false
                }
                onChange={() => {
                  onChange('isHomeless', !values?.isHomeless);
                }}
                disabled
              />
            }
            aria-label="Senza fissa dimora"
            label="Senza fissa dimora"
          />
        </Grid>
      ) : undefined}
      {ForPracticeTemporary ? (
        <Grid item xs={12} md={3}>
          <PmiButton fullWidth variant="contained" disabled={isPersonHomeless} color="secondary">
            VERIFICA DATI CATASTALI DA INDIRIZZO
          </PmiButton>
        </Grid>
      ) : undefined}
    </Grid>
  );
};

CadastralDataComponent.displayName = 'Cadastral Data Component';

const CadastralData = memo(CadastralDataComponent);

export default CadastralData;
